import { useQuery, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import {
  type LearnedPinyinSounds,
  collectLearnedPinyinSounds,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { type PronouncedGraphemes, usePronouncedGraphemesQueryOptions } from "#use-pronounced-graphemes-query";

/**
 * The initials, finals and tones the learner has learned: every sound of every reading they have a
 * pronunciation card in study for, whichever character it came with. Learning 本 běn teaches `b-`,
 * `-en` and the low tone for every other syllable too.
 */
export function selectLearnedPinyinSounds(pronounced: PronouncedGraphemes): LearnedPinyinSounds {
  // Keys are `${grapheme}\0${pinyin}`; graphemes never contain a NUL.
  return collectLearnedPinyinSounds(
    Array.from(pronounced.readings, (key) => key.slice(key.indexOf("\0") + 1)),
  );
}

/** The learner's learned pinyin sounds (`selectLearnedPinyinSounds`), `undefined` until loaded. */
export function useLearnedPinyinSoundsQuery() {
  return useQuery({
    ...usePronouncedGraphemesQueryOptions(),
    select: selectLearnedPinyinSounds,
  });
}

/**
 * Reads the learner's learned pinyin sounds when an action needs them (a reveal deciding what to
 * speak), from the cache while no card write has invalidated it.
 */
export function useFetchLearnedPinyinSounds(): () => Promise<LearnedPinyinSounds> {
  const queryClient = useQueryClient();
  const queryOptions = usePronouncedGraphemesQueryOptions();
  return async () => selectLearnedPinyinSounds(await queryClient.fetchQuery(queryOptions));
}
